/**
 * OnlineUsersIndicator
 *
 * Header pill that shows the number of online users in the caller's company.
 * Clicking it opens a popover listing each online teammate (avatar + name).
 *
 * Updates live via PresenceContext (no polling).
 */
import React, { useEffect, useState } from 'react';
import { Circle, Users } from 'lucide-react';
import { usePresence } from '../../context/PresenceContext';
import { useAuth } from '../../context/AuthContext';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';

export const OnlineUsersIndicator = () => {
  const { onlineUserIds, onlineCount } = usePresence();
  const { user, api } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch user details only when popover opens (lazy)
  const onOpenChange = async (open) => {
    if (!open || users.length > 0) return;
    if (onlineUserIds.size === 0) return;
    setLoading(true);
    try {
      // Pass only the ids that the indicator currently knows about
      const ids = Array.from(onlineUserIds).join(',');
      const res = await api.get(`/messages/users-by-ids?ids=${encodeURIComponent(ids)}`);
      setUsers(res.data?.users || []);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Reset cached user list when the online set changes — keeps popover fresh
  useEffect(() => {
    setUsers([]);
  }, [onlineUserIds]);

  if (!user?.user_id) return null;

  // Exclude self from the displayed count to make the number meaningful
  const otherCount = onlineUserIds.has(user.user_id) ? onlineCount - 1 : onlineCount;

  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-testid="online-users-indicator"
          className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
          title={`${otherCount} teammate${otherCount === 1 ? '' : 's'} online`}
          aria-label={`${otherCount} teammates online`}
        >
          <span className="relative inline-flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
          </span>
          <Users className="w-3 h-3" />
          <span data-testid="online-users-count">{otherCount}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-0 bg-slate-900 border-slate-800 text-white"
        data-testid="online-users-popover"
      >
        <div className="px-3 py-2 border-b border-slate-800 flex items-center justify-between">
          <span className="text-sm font-medium">Online now</span>
          <span className="text-xs text-slate-400">{otherCount}</span>
        </div>
        <ScrollArea className="max-h-72">
          <div className="py-1">
            {loading && (
              <div className="px-3 py-4 text-sm text-slate-400">Loading…</div>
            )}
            {!loading && users.length === 0 && (
              <div className="px-3 py-4 text-sm text-slate-400">
                No teammates online right now.
              </div>
            )}
            {!loading &&
              users
                .filter((u) => u.user_id !== user.user_id)
                .map((u) => (
                  <div
                    key={u.user_id}
                    data-testid={`online-user-${u.user_id}`}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800/60"
                  >
                    <div className="relative">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={u.avatar_url || undefined} alt={u.name} />
                        <AvatarFallback className="bg-slate-700 text-xs">
                          {(u.name || u.email || '?').slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <Circle className="absolute -bottom-0.5 -right-0.5 w-3 h-3 fill-emerald-500 text-emerald-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                      {u.role && (
                        <div className="text-xs text-slate-400 capitalize">
                          {u.role.replace(/_/g, ' ')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default OnlineUsersIndicator;
